import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
  Switch,
  RadioGroup,
  Radio,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as ExecuteIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  Code as QueryIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  TableChart as TableIcon,
  Storage as DatabaseIcon,
  ViewColumn as ColumnIcon,
  FindReplace as DuplicateIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { databaseAPI } from "../services/api.js";
import { useAuth } from "../contexts/AuthContext.jsx";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const QueryBuilder = () => {
  const { isAdmin, loading: authLoading } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Add loading check for auth context
  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Main tab state
  const [activeTab, setActiveTab] = useState(0);

  // Query builder state
  const [selectedDatabases, setSelectedDatabases] = useState([]);
  const [selectedSchemas, setSelectedSchemas] = useState([]); // New: Schema selection for admin
  const [selectedTables, setSelectedTables] = useState({});
  const [selectedColumns, setSelectedColumns] = useState({});
  const [recordLimit, setRecordLimit] = useState(0); // 0 means all rows
  const [whereConditions, setWhereConditions] = useState([]);
  const [orderBy, setOrderBy] = useState("");
  const [orderDirection, setOrderDirection] = useState("ASC");

  // Query state
  const [generatedQuery, setGeneratedQuery] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);

  // UI state
  const [showQuery, setShowQuery] = useState(false);

  // Duplicate management state
  const [duplicateSettings, setDuplicateSettings] = useState({
    databases: [],
    schemas: [], // New: Schema selection for duplicate manager
    tables: [],
    columns: [],
    matchType: "exact", // exact, fuzzy
    threshold: 0.8, // for fuzzy matching
  });
  const [duplicateResults, setDuplicateResults] = useState(null);
  const [duplicateTaskId, setDuplicateTaskId] = useState(null);
  const [selectedDuplicates, setSelectedDuplicates] = useState([]);
  const [duplicateProgress, setDuplicateProgress] = useState(0);
  const [isDuplicateScanning, setIsDuplicateScanning] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  // Duplicate Manager checkbox selections (mirror Query Builder)
  const [duplicateSelectedTables, setDuplicateSelectedTables] = useState({});

  // Remove admin-only restriction - now accessible to all users

  // Fetch databases (user-specific for regular users, all for admins)
  const { data: databasesData, isLoading: databasesLoading } = useQuery({
    queryKey: ["databases-query-builder"],
    queryFn: () => {
      if (!authLoading && isAdmin()) {
        return databaseAPI.listDatabases({ include_tables: true });
      } else {
        return databaseAPI.getUserTables({ includeTables: true });
      }
    },
    enabled: !authLoading,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch all user schemas (admin only)
  const { data: schemasData, isLoading: schemasLoading } = useQuery({
    queryKey: ["user-schemas"],
    queryFn: () => databaseAPI.listSchemas(),
    enabled: !authLoading && isAdmin(),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch tables based on selected schemas (admin only)
  const { data: schemaTablesData, isLoading: schemaTablesLoading } = useQuery({
    queryKey: ["schema-tables", selectedSchemas],
    queryFn: () => databaseAPI.getSchemaTables(selectedSchemas),
    enabled: !authLoading && isAdmin() && selectedSchemas.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Get selected table identifiers for column fetching
  const selectedTableIdentifiers = Object.keys(selectedTables).filter(
    (tableId) => selectedTables[tableId]
  );

    // Fetch columns for selected tables (for both admin and non-admin users)
  const { data: tableColumnsData, isLoading: columnsLoading } = useQuery({
    queryKey: ["table-columns", selectedTableIdentifiers, selectedSchemas],
    queryFn: async () => {
      if (selectedTableIdentifiers.length === 0) {
        return { table_columns: {} };
      }

      if (!authLoading && isAdmin() && selectedSchemas.length > 0) {
        // Use schema-based column fetching for admin with schema selection
        try {
          const response = await databaseAPI.getSchemaTablesColumns(
            selectedTableIdentifiers
          );
          // Ensure consistent data structure
          return {
            table_columns: response.data?.table_columns || response.table_columns || {}
          };
        } catch (error) {
          console.error('Error fetching schema table columns:', error);
          return { table_columns: {} };
        }
      } else {
        // Use database-based column fetching for both admin (without schema selection) and non-admin users
        const columnData = {};
        for (const tableKey of selectedTableIdentifiers) {
          const [first, second] = tableKey.split(".");
          let dbName, tableName;
          
          // Handle different key formats
          if (isAdmin() && selectedSchemas.length === 0) {
            // Admin without schema selection: key is "database.table"
            dbName = first;
            tableName = second;
          } else {
            // Non-admin users or admin with schema: key could be "database.table" or "schema.table" 
            // For non-admin users, first part is typically database name
            dbName = first;
            tableName = second;
          }
          
          try {
            const response = await databaseAPI.listColumns(dbName, tableName);
            const responseData = response.data || response;
            columnData[tableKey] = {
              schema_name: responseData.schema_name || first,
              table_name: tableName,
              columns: responseData.columns || [],
              column_count: (responseData.columns || []).length,
            };
          } catch (error) {
            console.error(`Error fetching columns for ${tableKey}:`, error);
            columnData[tableKey] = {
              schema_name: first,
              table_name: tableName,
              columns: [],
              column_count: 0,
              error: error.message
            };
          }
        }
        return { table_columns: columnData };
      }
    },
    enabled: !authLoading && selectedTableIdentifiers.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch table details for selected databases/schemas (Query Builder)
  const { data: tablesData, isLoading: tablesLoading } = useQuery({
    queryKey: ["tables-query-builder", selectedDatabases, selectedSchemas],
    queryFn: async () => {
      if (selectedDatabases.length === 0) return { tables: [] };

      const allTables = [];

      if (!authLoading && isAdmin()) {
        // Admin users: Check if schemas are selected for multi-schema view
        if (selectedSchemas.length > 0) {
          // Use schema-based table fetching
          try {
            const response = await databaseAPI.getSchemaTables(selectedSchemas);
            const tables = response.data.tables.map((table) => ({
              ...table,
              database_name: table.database_name || "google_db",
            }));
            allTables.push(...tables);
          } catch (error) {
            console.error("Error fetching tables for selected schemas:", error);
          }
        } else {
          // Fallback to database-based fetching (legacy behavior)
          for (const dbName of selectedDatabases) {
            try {
              const response = await databaseAPI.listTables(dbName, {
                include_columns: true,
              });
              const tables = response.data.tables.map((table) => ({
                ...table,
                database_name: dbName,
              }));
              allTables.push(...tables);
            } catch (error) {
              console.error(`Error fetching tables for ${dbName}:`, error);
            }
          }
        }
      } else {
        // Non-admin users: use tables from user-specific endpoint (their own schema only)
        const userDatabases =
          databasesData?.databases || databasesData?.data?.databases;
        if (userDatabases) {
          for (const database of userDatabases) {
            if (
              selectedDatabases.includes(database.database_name) &&
              database.tables
            ) {
              const tables = database.tables.map((table) => ({
                ...table,
                database_name: database.database_name,
                // Ensure schema_name is set for non-admin users
                schema_name: table.schema_name || database.schema_name,
              }));
              allTables.push(...tables);
            }
          }
        }
      }

      // Remove duplicates based on database_name.schema_name.table_name combination
      const uniqueTables = allTables.filter((table, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            `${t.database_name}.${t.schema_name || ''}.${t.table_name}` ===
            `${table.database_name}.${table.schema_name || ''}.${table.table_name}`
        )
      );

      return { tables: uniqueTables };
    },
    enabled: !authLoading && selectedDatabases.length > 0 && databasesData?.databases,
    staleTime: 2 * 60 * 1000,
  });

  // Fetch table details for duplicate manager databases/schemas
  const { data: duplicateTablesData, isLoading: duplicateTablesLoading } =
    useQuery({
      queryKey: [
        "tables-duplicate-manager",
        duplicateSettings.databases,
        duplicateSettings.schemas,
      ],
      queryFn: async () => {
        if (duplicateSettings.databases.length === 0) return { tables: [] };

        const allTables = [];

        if (!authLoading && isAdmin() && duplicateSettings.schemas.length > 0) {
          // Use schema-based table fetching for admin
          try {
            const response = await databaseAPI.getSchemaTables(
              duplicateSettings.schemas
            );
            const tables = response.data.tables.map((table) => ({
              ...table,
              database_name: table.database_name || "google_db",
            }));
            allTables.push(...tables);
          } catch (error) {
            console.error(
              "Error fetching tables for duplicate manager schemas:",
              error
            );
          }
        } else {
          // Fallback to database-based fetching
          for (const dbName of duplicateSettings.databases) {
            try {
              const response = await databaseAPI.listTables(dbName, {
                include_columns: true,
              });
              const tables = response.data.tables.map((table) => ({
                ...table,
                database_name: dbName,
              }));
              allTables.push(...tables);
            } catch (error) {
              console.error(
                `Error fetching tables for duplicate manager ${dbName}:`,
                error
              );
            }
          }
        }
        
        // Remove duplicates based on database_name.schema_name.table_name combination
        const uniqueTables = allTables.filter((table, index, self) =>
          index ===
          self.findIndex(
            (t) =>
              `${t.database_name}.${t.schema_name || ''}.${t.table_name}` ===
              `${table.database_name}.${table.schema_name || ''}.${table.table_name}`
          )
        );
        
        return { tables: uniqueTables };
      },
      enabled: !authLoading && duplicateSettings.databases.length > 0,
      staleTime: 2 * 60 * 1000,
    });

  // Execute query mutation
  const executeQueryMutation = useMutation({
    mutationFn: (queryData) => {
      // Transform table selection to include schema information
      const transformedTables = Object.keys(selectedTables)
        .filter((key) => selectedTables[key])
        .map((tableKey) => {
          const [first, second] = tableKey.split(".");
          const tableName = second;

          // Determine whether the first segment is a schema or a database
          const adminSchemaMode = !authLoading && isAdmin() && selectedSchemas.length > 0;

          let schemaName = null;
          let databaseName = null;
          let tableMeta = null;

          if (adminSchemaMode) {
            // In schema mode, the key is `${schema}.${table}` and we use the single DB (google_db)
            schemaName = first;
            databaseName = selectedDatabases[0] || "google_db";
            tableMeta = tables.find(
              (t) => t.schema_name === schemaName && t.table_name === tableName
            );
          } else {
            // Legacy mode, the key is `${database}.${table}`
            databaseName = first;
            tableMeta = tables.find(
              (t) => t.database_name === databaseName && t.table_name === tableName
            );
            schemaName = tableMeta?.schema_name || null;
          }

          return {
            name: tableName,
            database: databaseName,
            schema: schemaName,
            full_name: schemaName ? `${schemaName}.${tableName}` : tableName,
          };
        });

      // Build per-table columns map - use multiple key formats for robustness
      const columnsMap = {};
      let hasAnyColumnSelection = false;
      
      Object.entries(selectedColumns).forEach(([tableKey, cols]) => {
        if (cols && cols.length > 0) {
          hasAnyColumnSelection = true;
          const [first, second] = tableKey.split('.');
          const adminSchemaMode = !authLoading && isAdmin() && selectedSchemas.length > 0;
          
          // Always add the original tableKey format
          columnsMap[tableKey] = cols;
          
          if (adminSchemaMode) {
            // In admin schema mode: tableKey is "schema.table"
            const schemaName = first;
            const tableName = second;
            const key = `${schemaName}.${tableName}`;
            columnsMap[key] = cols;
          } else {
            // In regular mode: tableKey could be "database.table", need to find schema
            const databaseName = first;
            const tableName = second;
            const table = tables.find(t => t.database_name === databaseName && t.table_name === tableName);
            const schemaName = table?.schema_name;
            
            // Add multiple possible key formats to ensure backend can find it
            if (schemaName) {
              const schemaKey = `${schemaName}.${tableName}`;
              columnsMap[schemaKey] = cols;
            }
            
            // Also add just the table name as fallback
            columnsMap[tableName] = cols;
          }
        }
      });

      // Map where conditions to backend filters format
      const filters = (whereConditions || [])
        .filter((cond) => cond.column && cond.operator && cond.value !== undefined && cond.value !== null)
        .map((cond) => ({
          column: cond.column,
          operator: cond.operator,
          value: cond.value,
        }));

      const enhancedQueryData = {
        ...queryData,
        tables: transformedTables,
        columns_map: hasAnyColumnSelection ? columnsMap : null, // Send null when no specific columns selected
        databases: selectedDatabases,
        filters,
        user_context: true, // All users now need user-specific context for isolation
        combine_mode: 'union',
      };



      return databaseAPI.executeQuery(enhancedQueryData);
    },
    onSuccess: (data) => {
      // Navigate to results page with query data
      navigate("/query-results", {
        state: {
          queryData: {
            selectedDatabases,
            selectedTables,
            selectedColumns,
            recordLimit,
            whereConditions,
            orderBy,
            orderDirection,
          },
          queryResults: data.data,
          generatedQuery,
        },
      });
      enqueueSnackbar("Query executed successfully", { variant: "success" });
      setIsExecuting(false);
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to execute query",
        { variant: "error" }
      );
      setIsExecuting(false);
    },
  });

  // Find duplicates mutation
  const findDuplicatesMutation = useMutation({
    mutationFn: (duplicateData) => databaseAPI.findDuplicates(duplicateData),
    onSuccess: (data) => {
      setDuplicateTaskId(data.data.task_id);
      setIsDuplicateScanning(true);
      enqueueSnackbar("Duplicate detection started", { variant: "info" });
      // Start polling for results
      pollDuplicateResults(data.data.task_id);
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to start duplicate detection",
        { variant: "error" }
      );
      setIsDuplicateScanning(false);
    },
  });

  // Remove duplicates mutation
  const removeDuplicatesMutation = useMutation({
    mutationFn: (removeData) => databaseAPI.removeDuplicates(removeData),
    onSuccess: (data) => {
      enqueueSnackbar("Duplicate removal started", { variant: "info" });
      // Clear selected duplicates
      setSelectedDuplicates([]);
      // Refresh duplicate results after removal
      setTimeout(() => {
        if (data.data?.task_id) {
          pollDuplicateResults(data.data.task_id);
        } else if (duplicateTaskId) {
          pollDuplicateResults(duplicateTaskId);
        }
      }, 2000);
    },
    onError: (error) => {
      enqueueSnackbar(
        error.response?.data?.error || "Failed to remove duplicates",
        { variant: "error" }
      );
    },
  });

  const databases =
    databasesData?.data?.databases || databasesData?.databases || [];
  const tables = tablesData?.tables || [];
  const duplicateTables = duplicateTablesData?.tables || [];

  // Helper: find a safe common join column across selected tables (by preference)
  const findCommonJoinColumn = useCallback(() => {
    const selectedTableKeys = Object.keys(selectedTables).filter((key) => selectedTables[key]);
    if (selectedTableKeys.length < 2) return null;

    // Build column name sets per table using tableColumnsData (admin mode) or fallback to selectedColumns
    const columnSets = [];
    selectedTableKeys.forEach((tableKey) => {
      let cols = [];
      const tableCols = tableColumnsData?.data?.table_columns?.[tableKey] ||
        tableColumnsData?.table_columns?.[tableKey];
      if (tableCols && Array.isArray(tableCols.columns)) {
        cols = tableCols.columns.map((c) => (c.column_name || c).toString().toLowerCase());
      } else if (selectedColumns[tableKey]) {
        cols = selectedColumns[tableKey].map((c) => c.toString().toLowerCase());
      }
      columnSets.push(new Set(cols));
    });

    if (columnSets.length === 0) return null;
    const intersection = columnSets.reduce((acc, set) => {
      return new Set([...acc].filter((x) => set.has(x)));
    });

    const preference = ["id", "email", "phone"];
    const preferred = preference.find((p) => intersection.has(p));
    if (preferred) return preferred;
    return intersection.size > 0 ? [...intersection].sort()[0] : null;
  }, [selectedTables, selectedColumns, tableColumnsData]);

  // Generate SQL query based on selections
  const generateQuery = useCallback(() => {
    if (selectedDatabases.length === 0) return "";

    // Detect admin schema mode and resolve selected table refs
    const adminSchemaMode = !authLoading && isAdmin() && selectedSchemas.length > 0;
    const selectedTableKeys = Object.keys(selectedTables).filter((key) => selectedTables[key]);
    
    if (selectedTableKeys.length === 0) return "";
    
    const resolvedTables = selectedTableKeys.map((tableKey) => {
      if (adminSchemaMode) {
        const [schema, tableName] = tableKey.split(".");
        return { schema, name: tableName, ref: `"${schema}"."${tableName}"`, key: tableKey };
      }
      const [dbName, tableName] = tableKey.split(".");
      const table = tables.find(
        (t) => t.database_name === dbName && t.table_name === tableName
      );
      const schema = table?.schema_name || null;
      const ref = schema ? `"${schema}"."${tableName}"` : `"${tableName}"`;
      return { schema, name: tableName, ref, key: tableKey };
    });

    // Check if we have specific column selections
    const hasColumnSelections = Object.values(selectedColumns).some(cols => cols && cols.length > 0);
    
    let query = "";
    
    if (resolvedTables.length === 1) {
      // Single table query
      const table = resolvedTables[0];
      const tableColumns = selectedColumns[table.key] || [];
      
      if (tableColumns.length > 0) {
        // Specific columns selected
        const columnList = tableColumns.map(col => `"${col}"`).join(", ");
        query = `SELECT ${columnList} FROM ${table.ref}`;
      } else {
        // All columns
        query = `SELECT * FROM ${table.ref}`;
      }
    } else {
      // Multiple tables - let backend handle the complex logic
      // Generate a simple preview query showing selected tables
      const tableNames = resolvedTables.map(table => table.name).join(", ");
      
      // Check if any columns are specifically selected
      const hasColumnSelections = Object.values(selectedColumns).some(cols => cols && cols.length > 0);
      
      if (hasColumnSelections) {
        // Show which columns are selected from which tables
        const tableDetails = resolvedTables.map(table => {
          const tableColumns = selectedColumns[table.key] || [];
          if (tableColumns.length > 0) {
            return `${table.name} (${tableColumns.join(', ')})`;
          } else {
            return `${table.name} (all columns)`;
          }
        }).join(', ');
        
        query = `-- Multi-table query with specific column selection\n-- Tables: ${tableDetails}\n-- Backend will display selected columns side-by-side from all tables`;
      } else {
        // Simple table selection without specific columns
        query = `-- Multi-table query - All Columns\n-- Tables: ${tableNames}\n-- Backend will display all columns from all selected tables`;
      }
    }

    // Add WHERE conditions (only for single table queries in preview)
    if (resolvedTables.length === 1 && whereConditions.length > 0) {
      const validConditions = whereConditions.filter(
        (cond) => cond.column && cond.operator && cond.value
      );
      if (validConditions.length > 0) {
        query += " WHERE ";
        const conditionStrings = validConditions.map((cond) => {
          const value =
            cond.operator === "LIKE" ? `'%${cond.value}%'` : `'${cond.value}'`;
          return `"${cond.column}" ${cond.operator} ${value}`;
        });
        query += conditionStrings.join(" AND ");
      }
    }

    // Add ORDER BY (only for single table queries in preview)
    if (resolvedTables.length === 1 && orderBy) {
      query += ` ORDER BY "${orderBy}" ${orderDirection}`;
    }

    // Add LIMIT (only for single table queries in preview)
    if (resolvedTables.length === 1 && recordLimit > 0) {
      query += ` LIMIT ${recordLimit}`;
    }

    return query;
  }, [
    selectedDatabases,
    selectedTables,
    selectedColumns,
    whereConditions,
    orderBy,
    orderDirection,
    recordLimit,
    authLoading,
    isAdmin,
    selectedSchemas,
    tables
  ]);

  // Auto-select database for all users (including admin)
  useEffect(() => {
    // Auto-selection check for all users - admin users now also get isolated view

    if (databases.length === 1 && selectedDatabases.length === 0) {
      // Auto-selecting database for user (admin users now also isolated)
      setSelectedDatabases([databases[0].database_name]);
    }
  }, [databases, selectedDatabases.length]);

  // Update generated query when selections change
  useEffect(() => {
    const query = generateQuery();
    setGeneratedQuery(query);
  }, [
    selectedDatabases,
    selectedTables,
    selectedColumns,
    whereConditions,
    orderBy,
    orderDirection,
    recordLimit,
  ]);

  // Duplicate management helper functions
  const pollDuplicateResults = async (taskId) => {
    const maxAttempts = 30; // 5 minutes with 10-second intervals
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await databaseAPI.getDuplicateResults(taskId);
        // Duplicate detection response received
        const { state, progress, results, error } = response.data;

        if (state === "PROGRESS") {
          setDuplicateProgress(progress || 0);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, 10000); // Poll every 10 seconds
          } else {
            setIsDuplicateScanning(false);
            enqueueSnackbar("Duplicate detection timed out", {
              variant: "warning",
            });
          }
        } else if (state === "SUCCESS") {
          // Duplicate detection completed successfully

          setDuplicateResults(results);
          // Handle different response structures for duplicate results
          let groups = [];
          if (results && typeof results === "object") {
            // Check for nested results structure
            groups =
              results.results?.duplicate_groups ||
              results.duplicate_groups ||
              results.duplicateGroups ||
              results.groups ||
              [];
          }
          // Setting duplicate groups in state
          setDuplicateGroups(groups);
          setIsDuplicateScanning(false);
          setDuplicateProgress(100);

          // Check if this is a removal operation result
          if (results && results.removed_count !== undefined) {
            enqueueSnackbar(
              `Successfully removed ${results.removed_count} duplicate records`,
              { variant: "success" }
            );
            // Refresh the duplicate detection to show updated results
            setTimeout(() => {
              handleStartDuplicateDetection();
            }, 1000);
          } else {
            // Navigate to duplicate results page with data
            navigate("/duplicate-results", {
              state: {
                duplicateData: {
                  databases: duplicateSettings.databases,
                  tables: duplicateSettings.tables,
                  matchType: duplicateSettings.matchType,
                },
                duplicateResults: results,
                duplicateTaskId: taskId,
              },
            });
            enqueueSnackbar(
              `Found ${
                results.total_groups || groups.length || 0
              } duplicate groups with ${
                results.total_duplicates || 0
              } total records`,
              { variant: "success" }
            );
          }
        } else if (state === "FAILURE") {
          setIsDuplicateScanning(false);
          enqueueSnackbar(error || "Duplicate detection failed", {
            variant: "error",
          });
        }
      } catch (error) {
        console.error("Error polling duplicate results:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 10000);
        } else {
          setIsDuplicateScanning(false);
          enqueueSnackbar("Failed to get duplicate results", {
            variant: "error",
          });
        }
      }
    };

    poll();
  };

  const handleDuplicateSettingsChange = (field, value) => {
    setDuplicateSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleStartDuplicateDetection = () => {
    if (duplicateSettings.databases.length === 0) {
      enqueueSnackbar("Please select at least one database", {
        variant: "warning",
      });
      return;
    }

    const chosenKeys = Object.keys(duplicateSelectedTables).filter(
      (k) => duplicateSelectedTables[k]
    );
    let schemaTables = [];
    let legacyTables = [];
    if (isAdmin() && duplicateSettings.schemas.length > 0) {
      schemaTables = chosenKeys; // keys are schema.table
      legacyTables = chosenKeys
        .map((schemaTable) => {
          const [schemaName, tableName] = schemaTable.split(".");
          const tableMeta = duplicateTables.find(
            (t) => t.schema_name === schemaName && t.table_name === tableName
          );
          if (tableMeta) return `${tableMeta.database_name}.${tableName}`;
          return null;
        })
        .filter(Boolean);
    } else {
      legacyTables = chosenKeys; // keys are db.table
    }

    const params = {
      databases: duplicateSettings.databases,
      tables: legacyTables,
      schema_tables: schemaTables,
      match_type: duplicateSettings.matchType,
    };

    // Show different messages based on selection
    if (chosenKeys.length > 0) {
      enqueueSnackbar(
        `Starting duplicate detection across ${chosenKeys.length} selected tables`,
        { variant: "info" }
      );
    } else {
      enqueueSnackbar(
        `Starting duplicate detection across all tables in ${duplicateSettings.databases.length} databases`,
        { variant: "info" }
      );
    }

    findDuplicatesMutation.mutate(params);
  };

  const handleDuplicateSelection = (groupId, recordId, selected) => {
    setSelectedDuplicates((prev) => {
      const key = `${groupId}_${recordId}`;
      if (selected) {
        return [...prev, key];
      } else {
        return prev.filter((item) => item !== key);
      }
    });
  };

  const handleBulkDuplicateSelection = (groupId, action) => {
    const group = duplicateGroups.find((g) => g.id === groupId);
    if (!group) return;

    if (action === "select_all") {
      const newSelections = group.records.map(
        (record) => `${groupId}_${record.record_id}`
      );
      setSelectedDuplicates((prev) => [
        ...new Set([...prev, ...newSelections]),
      ]);
    } else if (action === "select_first") {
      const firstRecord = group.records[0];
      if (firstRecord) {
        setSelectedDuplicates((prev) => [
          ...new Set([...prev, `${groupId}_${firstRecord.id}`]),
        ]);
      }
    } else if (action === "select_last") {
      const lastRecord = group.records[group.records.length - 1];
      if (lastRecord) {
        setSelectedDuplicates((prev) => [
          ...new Set([...prev, `${groupId}_${lastRecord.id}`]),
        ]);
      }
    } else if (action === "deselect_all") {
      setSelectedDuplicates((prev) =>
        prev.filter((item) => !item.startsWith(`${groupId}_`))
      );
    }
  };

  const handleRemoveSelectedDuplicates = () => {
    if (selectedDuplicates.length === 0) {
      enqueueSnackbar("Please select duplicates to remove", {
        variant: "warning",
      });
      return;
    }

    // Processing selected duplicates for removal

    const recordsToRemove = selectedDuplicates
      .map((key) => {
        const parts = key.split("_");
        const groupId = parts[0];
        const recordId = parts.slice(1).join("_"); // Join all parts except the first as recordId might contain underscores
        // Processing duplicate group for removal

        const group = duplicateGroups.find((g) => g.id == groupId); // Use == to handle string/number comparison
        // Found duplicate group for processing

        const record = group?.records.find((r) => r.record_id === recordId);
        // Found record for removal

        if (!record) {
          console.error("Record not found for key:", key);
          return null;
        }

        // Format the record ID as expected by backend: "database_table_recordid"
        const formattedId = `${record.database}_${record.table}_${recordId}`;
        // Formatted record ID for removal
        return formattedId;
      })
      .filter(Boolean);

    // Sending duplicate removal request
    removeDuplicatesMutation.mutate({ records_to_remove: recordsToRemove });
  };

  const toggleGroupExpansion = (groupId) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const handleDatabaseChange = (event) => {
    const value = event.target.value;
    setSelectedDatabases(typeof value === "string" ? value.split(",") : value);
    setSelectedTables({});
    setSelectedColumns({});
  };

  const handleTableChange = (tableKey, checked) => {
    setSelectedTables((prev) => ({
      ...prev,
      [tableKey]: checked,
    }));

    // Reset column selection for this table if unchecked
    if (!checked) {
      setSelectedColumns((prev) => {
        const newColumns = { ...prev };
        delete newColumns[tableKey];
        return newColumns;
      });
    }
  };

  const handleColumnChange = (tableKey, columns) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [tableKey]: columns,
    }));
  };

  const handleExecuteQuery = () => {
    const selectedTableKeys = Object.keys(selectedTables).filter((key) => selectedTables[key]);
    
    if (selectedTableKeys.length === 0) {
      enqueueSnackbar("Please select at least one table", { variant: "warning" });
      return;
    }

    setIsExecuting(true);
    
    // Use the existing executeQueryMutation.mutate call which already has the proper data transformation
    executeQueryMutation.mutate({
      query: generatedQuery,
      limit: recordLimit || undefined, // undefined means backend default (all)
      offset: 0,
      order_by: orderBy || undefined,
      order_direction: orderBy ? orderDirection : undefined,
    });
  };

  const handleClearAll = () => {
    setSelectedDatabases([]);
    setSelectedTables({});
    setSelectedColumns({});
    setWhereConditions([]);
    setOrderBy("");
    setGeneratedQuery("");
  };

  const addWhereCondition = () => {
    setWhereConditions((prev) => [
      ...prev,
      { column: "", operator: "=", value: "" },
    ]);
  };

  const updateWhereCondition = (index, field, value) => {
    setWhereConditions((prev) => {
      const newConditions = [...prev];
      newConditions[index] = { ...newConditions[index], [field]: value };
      return newConditions;
    });
  };

  const removeWhereCondition = (index) => {
    setWhereConditions((prev) => prev.filter((_, i) => i !== index));
  };

  // Get available columns for WHERE conditions
  const getAvailableColumns = () => {
    const columns = [];
    Object.entries(selectedColumns).forEach(([tableKey, tableCols]) => {
      if (tableCols && tableCols.length > 0) {
        const [dbName, tableName] = tableKey.split(".");
        tableCols.forEach((col) => {
          columns.push(`${tableName}.${col}`);
        });
      }
    });
    return columns;
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Query Builder & Duplicate Manager
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Build dynamic queries, filter data, and manage duplicates across
            your databases
          </Typography>
        </Box>
      </Box>

      {/* Main Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            icon={<QueryIcon />}
            label="Query Builder"
            iconPosition="start"
          />
          <Tab
            icon={<DuplicateIcon />}
            label={
              <Badge
                badgeContent={duplicateGroups.length || null}
                color="error"
                max={99}
              >
                Duplicate Manager
              </Badge>
            }
            iconPosition="start"
          />
        </Tabs>

        <CardContent>
          {/* Query Builder Tab */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              {/* Query Builder Panel */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <FilterIcon /> Query Configuration
                    </Typography>

                    {/* Database Selection */}
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Select Databases</InputLabel>
                      <Select
                        multiple
                        value={selectedDatabases}
                        onChange={handleDatabaseChange}
                        input={<OutlinedInput label="Select Databases" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                        MenuProps={MenuProps}
                        disabled={databasesLoading}
                      >
                        {databases.map((db) => (
                          <MenuItem key={db.id} value={db.database_name}>
                            <DatabaseIcon fontSize="small" sx={{ mr: 1 }} />
                            {db.database_name} ({db.total_tables} tables)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Schema Selection (Admin Only) */}
                    {isAdmin() && selectedDatabases.length > 0 && (
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Select User Schemas</InputLabel>
                        <Select
                          multiple
                          value={selectedSchemas}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSelectedSchemas(
                              typeof value === "string"
                                ? value.split(",")
                                : value
                            );
                            // Clear table and column selections when schemas change
                            setSelectedTables({});
                            setSelectedColumns({});
                          }}
                          input={<OutlinedInput label="Select User Schemas" />}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((schemaName) => {
                                const schema =
                                  schemasData?.data?.schemas?.find(
                                    (s) => s.schema_name === schemaName
                                  ) ||
                                  schemasData?.schemas?.find(
                                    (s) => s.schema_name === schemaName
                                  );
                                return (
                                  <Chip
                                    key={schemaName}
                                    label={schema?.display_name || schemaName}
                                    size="small"
                                    color={
                                      schema?.is_admin ? "secondary" : "primary"
                                    }
                                  />
                                );
                              })}
                            </Box>
                          )}
                          MenuProps={MenuProps}
                          disabled={schemasLoading}
                        >
                          {(
                            schemasData?.data?.schemas ||
                            schemasData?.schemas ||
                            []
                          ).map((schema) => (
                            <MenuItem
                              key={schema.schema_name}
                              value={schema.schema_name}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <DatabaseIcon fontSize="small" />
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" component="span">
                                      {schema.username}
                                    </Typography>
                                    {schema.is_admin && (
                                      <Chip
                                        label="Admin"
                                        size="small"
                                        color="secondary"
                                        sx={{ height: 20 }}
                                      />
                                    )}
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {schema.table_count} tables • {schema.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Select user schemas to view their tables. Leave empty
                          to use default database view.
                        </Typography>
                      </FormControl>
                    )}

                    {/* Table Selection */}
                    {selectedDatabases.length > 0 && (
                      <Accordion sx={{ mt: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">
                            <TableIcon
                              fontSize="small"
                              sx={{ mr: 1, verticalAlign: "middle" }}
                            />
                            Select Tables (
                            {
                              Object.values(selectedTables).filter(Boolean)
                                .length
                            }{" "}
                            selected)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {tablesLoading ? (
                            <CircularProgress size={24} />
                          ) : (
                            <Box>
                              {isAdmin() && selectedSchemas.length > 0
                                ? // Admin with schema selection: Group tables by schema
                                  selectedSchemas.map((schemaName) => {
                                    const schemaTables = tables.filter(
                                      (t) => t.schema_name === schemaName
                                    );
                                    const schema =
                                      schemasData?.data?.schemas?.find(
                                        (s) => s.schema_name === schemaName
                                      ) ||
                                      schemasData?.schemas?.find(
                                        (s) => s.schema_name === schemaName
                                      );

                                    return (
                                      <Box key={schemaName} sx={{ mb: 3 }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                          }}
                                        >
                                          <DatabaseIcon fontSize="small" />
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                          <Typography
                                            variant="subtitle2"
                                            color="primary"
                                            component="span"
                                          >
                                            {schema?.username || schemaName}
                                          </Typography>
                                          {schema?.is_admin && (
                                            <Chip
                                              label="Admin"
                                              size="small"
                                              color="secondary"
                                            />
                                          )}
                                        </Box>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            component="span"
                                          >
                                            ({schemaTables.length} tables)
                                          </Typography>
                                        </Box>
                                        {schemaTables.map((table) => {
                                          const tableKey = `${schemaName}.${table.table_name}`;
                                          return (
                                            <FormControlLabel
                                              key={tableKey}
                                              control={
                                                <Checkbox
                                                  checked={
                                                    selectedTables[tableKey] ||
                                                    false
                                                  }
                                                  onChange={(e) =>
                                                    handleTableChange(
                                                      tableKey,
                                                      e.target.checked
                                                    )
                                                  }
                                                />
                                              }
                                              label={
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {table.table_name}
                                                  </Typography>
                                                  <Chip
                                                    label={`${
                                                      table.record_count || 0
                                                    } rows`}
                                                    size="small"
                                                    variant="outlined"
                                                  />
                                                  {table.user_context && (
                                                    <Chip
                                                      label={
                                                        table.user_context
                                                          .username
                                                      }
                                                      size="small"
                                                      color="info"
                                                      variant="outlined"
                                                    />
                                                  )}
                                                </Box>
                                              }
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                ml: 2,
                                                mb: 1,
                                              }}
                                            />
                                          );
                                        })}
                                      </Box>
                                    );
                                  })
                                : // Regular users or admin without schema selection: Group by database
                                  selectedDatabases.map((dbName) => {
                                    const dbTables = tables.filter(
                                      (t) => t.database_name === dbName
                                    );
                                    return (
                                      <Box key={dbName} sx={{ mb: 2 }}>
                                        <Typography
                                          variant="subtitle2"
                                          color="primary"
                                          gutterBottom
                                        >
                                          {dbName}
                                        </Typography>
                                        {dbTables.map((table) => {
                                          const tableKey = `${dbName}.${table.table_name}`;
                                          const reactKey = `${dbName}.${table.schema_name || ''}.${table.table_name}`;
                                          return (
                                            <FormControlLabel
                                              key={reactKey}
                                              control={
                                                <Checkbox
                                                  checked={
                                                    selectedTables[tableKey] ||
                                                    false
                                                  }
                                                  onChange={(e) =>
                                                    handleTableChange(
                                                      tableKey,
                                                      e.target.checked
                                                    )
                                                  }
                                                />
                                              }
                                              label={
                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                  }}
                                                >
                                                  <Typography variant="body2">
                                                    {table.table_name}
                                                  </Typography>
                                                  <Chip
                                                    label={`${
                                                      table.record_count || 0
                                                    } rows`}
                                                    size="small"
                                                    variant="outlined"
                                                  />
                                                </Box>
                                              }
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                ml: 2,
                                                mb: 1,
                                              }}
                                            />
                                          );
                                        })}
                                      </Box>
                                    );
                                  })}
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Column Selection */}
                    {Object.keys(selectedTables).some(
                      (key) => selectedTables[key]
                    ) && (
                      <Accordion sx={{ mt: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">
                            <ColumnIcon
                              fontSize="small"
                              sx={{ mr: 1, verticalAlign: "middle" }}
                            />
                            Select Columns (optional - leave empty for all
                            columns)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {columnsLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CircularProgress size={20} />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Loading columns...
                              </Typography>
                            </Box>
                          ) : (
                            Object.entries(selectedTables)
                              .filter(([key, selected]) => selected)
                              .map(([tableKey]) => {
                                // Get column data from the API response with consistent access pattern
                                const allColumnsData = tableColumnsData?.table_columns || {};
                                const tableColumns = allColumnsData[tableKey];

                                // Enhanced error handling and debugging
                                if (!tableColumns) {
                                  console.warn(`No column data found for table: ${tableKey}`);
                                  console.debug('Available tables in column data:', Object.keys(allColumnsData));
                                  return (
                                    <Box key={tableKey} sx={{ mb: 2 }}>
                                      <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        gutterBottom
                                      >
                                        {tableKey.split(".")[1] || tableKey}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {tableColumns?.error ? 
                                          `Error loading columns: ${tableColumns.error}` :
                                          'Loading columns...'
                                        }
                                      </Typography>
                                    </Box>
                                  );
                                }

                                if (!tableColumns.columns || !Array.isArray(tableColumns.columns) || tableColumns.columns.length === 0) {
                                  return (
                                    <Box key={tableKey} sx={{ mb: 2 }}>
                                      <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        gutterBottom
                                      >
                                        {tableColumns.table_name || tableKey.split(".")[1] || tableKey}
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                      >
                                        {tableColumns.error ? 
                                          `Error: ${tableColumns.error}` :
                                          'No columns available for this table'
                                        }
                                      </Typography>
                                    </Box>
                                  );
                                }

                                return (
                                  <Box key={tableKey} sx={{ mb: 2 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 1,
                                      }}
                                    >
                                      <TableIcon fontSize="small" />
                                      <Typography
                                        variant="subtitle2"
                                        color="primary"
                                        component="span"
                                      >
                                        {tableColumns.schema_name}.
                                        {tableColumns.table_name}
                                      </Typography>
                                      <Chip
                                        label={`${tableColumns.column_count} columns`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </Box>
                                    <FormControl fullWidth size="small">
                                      <InputLabel>
                                        Select Columns (Optional)
                                      </InputLabel>
                                      <Select
                                        multiple
                                        value={selectedColumns[tableKey] || []}
                                        onChange={(e) =>
                                          handleColumnChange(
                                            tableKey,
                                            e.target.value
                                          )
                                        }
                                        input={
                                          <OutlinedInput label="Select Columns (Optional)" />
                                        }
                                        renderValue={(selected) => (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              flexWrap: "wrap",
                                              gap: 0.5,
                                            }}
                                          >
                                            {selected.map((value) => (
                                              <Chip
                                                key={value}
                                                label={value}
                                                size="small"
                                                color="primary"
                                              />
                                            ))}
                                          </Box>
                                        )}
                                        MenuProps={MenuProps}
                                      >
                                        {tableColumns.columns.map((column) => {
                                          // Handle both object and string column formats
                                          const columnName = typeof column === 'string' ? column : (column.column_name || column.name);
                                          const dataType = typeof column === 'object' ? (column.data_type || column.type) : null;
                                          
                                          return (
                                            <MenuItem
                                              key={columnName}
                                              value={columnName}
                                            >
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                }}
                                              >
                                                <Box component="span" sx={{ fontSize: '0.875rem' }}>
                                                  {columnName}
                                                </Box>
                                                {dataType && (
                                                  <Chip
                                                    label={dataType}
                                                    size="small"
                                                    variant="outlined"
                                                    color="secondary"
                                                  />
                                                )}
                                              </Box>
                                            </MenuItem>
                                          );
                                        })}
                                      </Select>
                                    </FormControl>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ mt: 1 }}
                                    >
                                      Leave empty to select all columns from
                                      this table
                                    </Typography>
                                  </Box>
                                );
                              })
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Query Options */}
                    <Accordion sx={{ mt: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                          Query Options
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Rows to Display</InputLabel>
                              <Select
                                label="Rows to Display"
                                value={recordLimit}
                                onChange={(e) => setRecordLimit(Number(e.target.value))}
                              >
                                <MenuItem value={0}>All rows</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                                <MenuItem value={250}>250</MenuItem>
                                <MenuItem value={500}>500</MenuItem>
                                <MenuItem value={1000}>1000</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Order Direction</InputLabel>
                              <Select
                                value={orderDirection}
                                onChange={(e) => setOrderDirection(e.target.value)}
                                label="Order Direction"
                              >
                                <MenuItem value="ASC">Ascending</MenuItem>
                                <MenuItem value="DESC">Descending</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          {/* Optional: order-by column dropdown populated from selected columns */}
                          {/* Keeping default order (DB natural) when no column chosen */}
                          {/*
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Order By Column</InputLabel>
                              <Select
                                value={orderBy}
                                label="Order By Column"
                                onChange={(e) => setOrderBy(e.target.value)}
                              >
                                <MenuItem value="">Default (table order)</MenuItem>
                                {getAvailableColumns().map((c) => (
                                  <MenuItem key={c} value={c.split('.')[1] || c}>{c}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          */}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>

                    {/* Execute Button */}
                    <Box sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        startIcon={
                          isExecuting ? (
                            <CircularProgress size={16} />
                          ) : (
                            <ExecuteIcon />
                          )
                        }
                        onClick={handleExecuteQuery}
                        disabled={!generatedQuery || isExecuting}
                        fullWidth
                      >
                        {isExecuting ? "Executing..." : "Execute Query"}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Duplicate Manager Tab */}
          {activeTab === 1 && (
            <Grid container spacing={3}>
              {/* Duplicate Detection Configuration - Full Width */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <SettingsIcon /> Duplicate Detection Settings
                    </Typography>

                    {/* Database Selection for Duplicates */}
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Select Databases</InputLabel>
                      <Select
                        multiple
                        value={duplicateSettings.databases}
                        onChange={(e) =>
                          handleDuplicateSettingsChange(
                            "databases",
                            e.target.value
                          )
                        }
                        input={<OutlinedInput label="Select Databases" />}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {databases.map((db) => (
                          <MenuItem key={db.id} value={db.database_name}>
                            <DatabaseIcon fontSize="small" sx={{ mr: 1 }} />
                            {db.database_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Schema Selection for Duplicates (Admin Only) */}
                    {isAdmin() && duplicateSettings.databases.length > 0 && (
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Select User Schemas</InputLabel>
                        <Select
                          multiple
                          value={duplicateSettings.schemas}
                          onChange={(e) => {
                            const value = e.target.value;
                            const newSchemas =
                              typeof value === "string"
                                ? value.split(",")
                                : value;
                            setDuplicateSettings((prev) => ({
                              ...prev,
                              schemas: newSchemas,
                              tables: [], // Clear table selection when schemas change
                              columns: [],
                            }));
                          }}
                          input={<OutlinedInput label="Select User Schemas" />}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((schemaName) => {
                                const schema =
                                  schemasData?.data?.schemas?.find(
                                    (s) => s.schema_name === schemaName
                                  ) ||
                                  schemasData?.schemas?.find(
                                    (s) => s.schema_name === schemaName
                                  );
                                return (
                                  <Chip
                                    key={schemaName}
                                    label={schema?.display_name || schemaName}
                                    size="small"
                                    color={
                                      schema?.is_admin ? "secondary" : "primary"
                                    }
                                  />
                                );
                              })}
                            </Box>
                          )}
                          MenuProps={MenuProps}
                          disabled={schemasLoading}
                        >
                          {(
                            schemasData?.data?.schemas ||
                            schemasData?.schemas ||
                            []
                          ).map((schema) => (
                            <MenuItem
                              key={schema.schema_name}
                              value={schema.schema_name}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <DatabaseIcon fontSize="small" />
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" component="span">
                                      {schema.username}
                                    </Typography>
                                    {schema.is_admin && (
                                      <Chip
                                        label="Admin"
                                        size="small"
                                        color="secondary"
                                        sx={{ height: 20 }}
                                      />
                                    )}
                                  </Box>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {schema.table_count} tables • {schema.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Select user schemas for duplicate detection. Leave
                          empty to use default database view.
                        </Typography>
                      </FormControl>
                    )}

                    {/* Table Selection for Duplicates (checkbox list) */}
                    {duplicateSettings.databases.length > 0 && (
                      <Accordion sx={{ mt: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="subtitle1">
                            <TableIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Select Tables for Duplicate Detection ({Object.values(duplicateSelectedTables).filter(Boolean).length} selected)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {duplicateTablesLoading ? (
                            <CircularProgress size={24} />
                          ) : isAdmin() && duplicateSettings.schemas.length > 0 ? (
                            duplicateSettings.schemas.map((schemaName) => {
                              const schemaTables = duplicateTables.filter((t) => t.schema_name === schemaName);
                              const schema =
                                schemasData?.data?.schemas?.find((s) => s.schema_name === schemaName) ||
                                schemasData?.schemas?.find((s) => s.schema_name === schemaName);
                              return (
                                <Box key={`dup-schema-${schemaName}`} sx={{ mb: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <DatabaseIcon fontSize="small" />
                                    <Typography variant="subtitle2" color="primary" component="span">
                                      {schema?.username || schemaName}
                                    </Typography>
                                  </Box>
                                  {schemaTables.map((table) => {
                                    const key = `${schemaName}.${table.table_name}`;
                                    const reactKey = `${table.database_name}.${schemaName}.${table.table_name}`;
                                    return (
                                      <FormControlLabel
                                        key={reactKey}
                                        control={
                                          <Checkbox
                                            checked={duplicateSelectedTables[key] || false}
                                            onChange={(e) =>
                                              setDuplicateSelectedTables((prev) => ({ ...prev, [key]: e.target.checked }))
                                            }
                                          />
                                        }
                                        label={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2">{table.table_name}</Typography>
                                            <Chip label={`${table.record_count || 0} rows`} size="small" variant="outlined" />
                                          </Box>
                                        }
                                        sx={{ ml: 2, mb: 1 }}
                                      />
                                    );
                                  })}
                                </Box>
                              );
                            })
                          ) : (
                            duplicateSettings.databases.map((dbName) => {
                              const dbTables = duplicateTables.filter((t) => t.database_name === dbName);
                              return (
                                <Box key={`dup-db-${dbName}`} sx={{ mb: 2 }}>
                                  <Typography variant="subtitle2" color="primary" gutterBottom>
                                    {dbName}
                                  </Typography>
                                  {dbTables.map((table) => {
                                    const key = `${dbName}.${table.table_name}`;
                                    const reactKey = `${dbName}.${table.schema_name || ''}.${table.table_name}`;
                                    return (
                                      <FormControlLabel
                                        key={reactKey}
                                        control={
                                          <Checkbox
                                            checked={duplicateSelectedTables[key] || false}
                                            onChange={(e) =>
                                              setDuplicateSelectedTables((prev) => ({ ...prev, [key]: e.target.checked }))
                                            }
                                          />
                                        }
                                        label={
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2">{table.table_name}</Typography>
                                            <Chip label={`${table.record_count || 0} rows`} size="small" variant="outlined" />
                                          </Box>
                                        }
                                        sx={{ ml: 2, mb: 1 }}
                                      />
                                    );
                                  })}
                                </Box>
                              );
                            })
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Match Type Selection */}
                    <FormControl component="fieldset" margin="normal">
                      <FormLabel component="legend">Match Type</FormLabel>
                      <RadioGroup
                        value={duplicateSettings.matchType}
                        onChange={(e) =>
                          handleDuplicateSettingsChange(
                            "matchType",
                            e.target.value
                          )
                        }
                      >
                        <FormControlLabel
                          value="exact"
                          control={<Radio />}
                          label="Exact Match"
                        />
                        <FormControlLabel
                          value="fuzzy"
                          control={<Radio />}
                          label="Fuzzy Match"
                        />
                      </RadioGroup>
                    </FormControl>

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
                      <Button
                        variant="contained"
                        startIcon={
                          isDuplicateScanning ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SearchIcon />
                          )
                        }
                        onClick={handleStartDuplicateDetection}
                        disabled={
                          isDuplicateScanning ||
                          duplicateSettings.databases.length === 0
                        }
                      >
                        {isDuplicateScanning
                          ? "Scanning..."
                          : duplicateSettings.tables &&
                            duplicateSettings.tables.length > 0
                          ? `Find Duplicates in Selected Tables (${duplicateSettings.tables.length})`
                          : "Find Duplicates Across All Tables"}
                      </Button>
                    </Box>

                    {/* Progress Bar */}
                    {isDuplicateScanning && (
                      <Box sx={{ mt: 2 }}>
                        <LinearProgress
                          variant="determinate"
                          value={duplicateProgress}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Scanning for duplicates... {duplicateProgress}%
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Generated Query Dialog */}
      <Dialog
        open={showQuery}
        onClose={() => setShowQuery(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Generated SQL Query</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={generatedQuery}
            variant="outlined"
            InputProps={{
              readOnly: true,
              style: { fontFamily: "monospace", fontSize: "14px" },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowQuery(false)}>Close</Button>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(generatedQuery);
              enqueueSnackbar("Query copied to clipboard", {
                variant: "success",
              });
            }}
            variant="contained"
          >
            Copy Query
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueryBuilder;
